import { Fragment } from "react";
import styled from "styled-components";
import LinkButton from "common/Buttons/LinkButton";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { NextArrow } from "features/onboarding/common/OnboardingIcons";
import { ONBOARDING_CONTINUE_LIVE_ID } from "testIds";

export interface WalkthroughRoute {
  label: string;
  onClick: () => void;
  testId: string;
  loading?: boolean;
}

/**
 * Every other route of the same choice on one centred line under the primary
 * button: "or browse presets · build my own". It may wrap on phones, but a
 * route never gets a line of its own.
 */
export const WalkthroughAlternatives = ({
  routes,
  lead = true,
}: {
  routes: WalkthroughRoute[];
  /** Starts the line with a muted "or" (off for a plain "back" link). */
  lead?: boolean;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  if (!routes.length) return null;
  return (
    <Alternatives>
      {routes.map((route, index) => (
        <Fragment key={route.testId}>
          {index > 0 && " "}
          <Route>
            {index === 0 && lead && <Muted>{t("walkthrough.or")} </Muted>}
            {index > 0 && <Muted aria-hidden>· </Muted>}
            <LinkButton
              type="link"
              loading={route.loading}
              onClick={route.onClick}
              data-testid={route.testId}
            >
              {route.label}
            </LinkButton>
          </Route>
        </Fragment>
      ))}
    </Alternatives>
  );
};

/** The live path, apart from the demo choice: after a hairline, quieter. */
export const WalkthroughLiveRow = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  return (
    <LiveRow>
      <Muted>{t("walkthrough.live_prompt")}</Muted>{" "}
      <LinkButton
        type="link"
        onClick={onClick}
        data-testid={ONBOARDING_CONTINUE_LIVE_ID}
      >
        {t("walkthrough.use_exchange")} <NextArrow />
      </LinkButton>
    </LiveRow>
  );
};

const Muted = styled.span`
  color: ${(p) => p.theme.colors.secondaryText};
`;
const Route = styled.span`
  white-space: nowrap;
`;
const Alternatives = styled.p`
  margin: 0;
  text-align: center;
  font-size: 14px;
  line-height: 20px;
  .ant-btn-link {
    height: auto;
    padding: 4px 2px;
    font-size: inherit;
    line-height: inherit;
    vertical-align: baseline;
  }
  @media (max-width: 560px) {
    .ant-btn-link {
      padding: 6px 2px;
    }
  }
`;
const LiveRow = styled.p`
  margin: 20px 0 0;
  padding-top: 16px;
  border-top: 1px solid var(--new-look-hairline);
  text-align: center;
  font-size: 13px;
  line-height: 20px;
  .ant-btn-link {
    height: auto;
    padding: 4px 2px;
    gap: 4px;
    font-size: inherit;
    line-height: inherit;
    vertical-align: baseline;
  }
  @media (max-width: 560px) {
    .ant-btn-link {
      padding: 6px 2px;
    }
  }
`;
